import React, { useState } from 'react';
import { api } from '../../services/api';
import { TurnstileWidget, turnstileEnabled } from '../Auth/TurnstileWidget';
import { EA_ROLLER, EaRol } from './constants/roller';

interface EduAnalizLoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAccess: (user: any) => void;
}

export const EduAnalizLoginModal: React.FC<EduAnalizLoginModalProps> = ({ isOpen, onClose, onAccess }) => {
    const [selectedRole, setSelectedRole] = useState<EaRol>('teacher');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [institutionCode, setInstitutionCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    // Jeton tek kullanımlık: başarısız denemeden sonra sıfırlanmazsa ikinci
    // deneme harcanmış jetonla gidiyor ve "robot doğrulaması başarısız" alıyor.
    const [turnstileSifirla, setTurnstileSifirla] = useState(0);
    const turnstileTazele = () => { setTurnstileToken(''); setTurnstileSifirla((n) => n + 1); };

    // Support Forms State
    const [view, setView] = useState<'login' | 'forgot_password' | 'tech_support'>('login');
    const [supportForm, setSupportForm] = useState({ name: '', email: '', message: '' });
    const [supportStatus, setSupportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    if (!isOpen) return null;

    const handleAccess = async () => {
        if (!email || !password || !institutionCode) {
            setError('Lütfen kurum kodu, e-posta ve şifrenizi girin.');
            return;
        }
        if (turnstileEnabled && !turnstileToken) {
            setError('Lütfen robot olmadığınızı doğrulayın.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const user = await api.login(email, password, true, selectedRole, institutionCode, turnstileToken || undefined);
            if (user) {
                onAccess(user);
                onClose();
            } else {
                setError('E-posta veya şifre hatalı!');
                turnstileTazele();
            }
        } catch (err: any) {
            setError(err.message || 'Giriş yapılamadı.');
            turnstileTazele();
        } finally {
            setLoading(false);
        }
    };

    const handleSupportSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSupportStatus('loading');
        
        // Form validations
        if (view === 'tech_support' && (!supportForm.name || !supportForm.email || !supportForm.message)) {
            setSupportStatus('error');
            setTimeout(() => setSupportStatus('idle'), 3000);
            return;
        }

        if (view === 'forgot_password' && !supportForm.email) {
            setSupportStatus('error');
            setTimeout(() => setSupportStatus('idle'), 3000);
            return;
        }

        try {
            await api.createSupportTicket({
                userId: 1,
                userName: supportForm.name || 'Misafir Kullanıcı',
                userRole: 'guest',
                userEmail: supportForm.email,
                subject: view === 'forgot_password' ? 'Şifre Sıfırlama Talebi' : 'Teknik Destek Talebi',
                category: view === 'forgot_password' ? 'general' : 'technical',
                priority: 'medium',
                message: supportForm.message || 'Şifre sıfırlama talebi gönderildi.'
            });
            setSupportStatus('success');
            setTimeout(() => {
                setView('login');
                setSupportStatus('idle');
                setSupportForm({ name: '', email: '', message: '' });
            }, 3000);
        } catch (err) {
            console.error('Support submission error:', err);
            setSupportStatus('error');
            setTimeout(() => setSupportStatus('idle'), 3000);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
            <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in">
                <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-[#0F2942] flex items-center gap-2.5">
                        <span className="text-[#0095FF]">
                            {view === 'login' ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                            ) : view === 'forgot_password' ? (
                                <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                            ) : (
                                <svg className="w-6 h-6 text-[#0095FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            )}
                        </span> 
                        {view === 'login' ? 'EduAnaliz Girişi' : view === 'forgot_password' ? 'Şifremi Unuttum' : 'Teknik Destek'}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" title="Kapat">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {view === 'login' ? (
                    <form onSubmit={(e) => { e.preventDefault(); handleAccess(); }} className="p-6 md:p-8 space-y-6">
                        <div className="space-y-2">
                            <label htmlFor="ea-rol" className="text-xs font-bold text-slate-600 uppercase tracking-wide">EduAnaliz Rolü Seçin</label>
                            <select
                                id="ea-rol"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value as EaRol)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm font-semibold"
                            >
                                {EA_ROLLER.map((r) => (
                                    <option key={r.id} value={r.id}>{r.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Kurum Kodu</label>
                            <input
                                type="text"
                                required
                                value={institutionCode}
                                onChange={(e) => setInstitutionCode(e.target.value.toUpperCase())}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm"
                                placeholder="Kurum Kodunuz"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">E-posta</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm"
                                placeholder="E-posta adresiniz"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Şifre</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm"
                                placeholder="Şifreniz"
                            />
                        </div>

                        {error && <p className="text-red-500 text-xs bg-red-50 p-3 border border-red-100 rounded-lg">{error}</p>}

                        <TurnstileWidget
                            resetKey={turnstileSifirla}
                            onVerify={(token) => setTurnstileToken(token)}
                            onExpire={() => setTurnstileToken('')}
                        />

                        <button
                            type="submit"
                            disabled={loading || (turnstileEnabled && !turnstileToken)}
                            className="w-full py-4 bg-[#0F2942] hover:bg-[#0095FF] text-white font-extrabold rounded-xl shadow-lg shadow-slate-900/15 transition-all transform hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50"
                        >
                            {loading ? 'Giriş Yapılıyor...' : 'Sisteme Giriş Yap'}
                        </button>

                        <div className="flex items-center justify-between text-xs mt-4 pt-4 border-t border-slate-100">
                            <button 
                                type="button" 
                                onClick={() => setView('forgot_password')} 
                                className="text-slate-500 hover:text-[#0095FF] transition-colors font-semibold"
                            >
                                Şifremi Unuttum
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setView('tech_support')} 
                                className="text-slate-500 hover:text-[#0095FF] transition-colors flex items-center gap-1 font-semibold"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                Teknik Ekibe Ulaş
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleSupportSubmit} className="p-6 md:p-8 space-y-6">
                        {supportStatus === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center animate-fade-in">
                                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 mb-2">Talebiniz Alındı</h3>
                                <p className="text-slate-600 text-sm">
                                    {view === 'forgot_password' 
                                        ? "Şifre sıfırlama bağlantısı e-posta adresinize gönderilecektir." 
                                        : "Teknik destek ekibimiz en kısa sürede sizinle iletişime geçecektir."}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 animate-fade-in">
                                <p className="text-xs text-slate-600 mb-4 font-medium">
                                    {view === 'forgot_password' 
                                        ? "Sisteme kayıtlı e-posta adresinizi giriniz. Şifre sıfırlama talimatları size iletilecektir." 
                                        : "Lütfen karşılaştığınız sorunu veya talebinizi detaylı bir şekilde açıklayınız."}
                                </p>

                                {view === 'tech_support' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Ad Soyad</label>
                                        <input
                                            type="text"
                                            required
                                            value={supportForm.name}
                                            onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm"
                                            placeholder="Adınız ve Soyadınız"
                                        />
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={supportForm.email}
                                        onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm"
                                        placeholder="E-posta adresiniz"
                                    />
                                </div>

                                {view === 'tech_support' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Mesajınız</label>
                                        <textarea
                                            required
                                            value={supportForm.message}
                                            onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                                            rows={4}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm resize-none"
                                            placeholder="Karşılaştığınız sorunu veya talebinizi buraya yazın..."
                                        />
                                    </div>
                                )}

                                {supportStatus === 'error' && (
                                    <p className="text-red-500 text-xs bg-red-50 p-3 border border-red-100 rounded-lg">Lütfen tüm alanları doldurunuz.</p>
                                )}

                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setView('login')}
                                        className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors shadow-sm text-xs"
                                    >
                                        Geri Dön
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={supportStatus === 'loading'}
                                        className="flex-1 py-3 bg-[#0F2942] hover:bg-[#0095FF] text-white font-extrabold rounded-xl shadow-md transition-all disabled:opacity-50 text-xs"
                                    >
                                        {supportStatus === 'loading' ? 'Gönderiliyor...' : 'Gönder'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};
